import Content from '../../types/enums/content';
import Focus from '../../types/enums/focus';
import Routes from '../../types/enums/routes';
import { PlayerState } from '../../types/enums/player';
import { clearModal } from '../slices/modal.slice';
import { close, open } from '../slices/floatingMenu.slice';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { popFocus, pushFocus } from '../slices/focus.slice';
import { seek } from '../slices/media.slice';
import { store } from '../store';
import {
  IFloatingMenuState,
  IMediaState,
  IModalState,
} from '../../types/interfaces/states';
import Modals from '@shared/types/enums/modals';

/**
 * Async thunk action to close the floating menu.
 * Closes the menu, clears modals (except pause ads), and pops the focus stack.
 *
 * @param {unknown} route - Route parameter (currently unused)
 * @param {AsyncThunk} thunk - Redux thunk API
 */
const closeFloatingMenu = createAsyncThunk('action/close', (route, thunk) => {
  try {
    const { floatingMenu } = store.getState();
    if (floatingMenu.open) {
      thunk.dispatch(close());
      thunk.dispatch(clearModal());
      thunk.dispatch(popFocus());
    }
  } catch (error) {
    thunk.rejectWithValue(error);
  }
});

/**
 * Async thunk action to open the floating menu.
 * Validates the route and asset, handles seeking state, and opens the menu with focus management.
 *
 * @param {IFloatingMenuState} route - The floating menu state configuration
 * @param {AsyncThunk} thunk - Redux thunk API
 */
const openFloatingMenu = createAsyncThunk(
  'action/open',
  (route: IFloatingMenuState, thunk) => {
    try {
      const { media, modal } = thunk.getState() as {
        media: IMediaState;
        modal: IModalState;
      };
      if (route?.asset?.dummy) return;
      if (modal?.current) return;
      if (
        route?.asset?.type === Content.ADDON ||
        route?.asset?.type === Content.CORNER
      )
        return;
      if (
        [Routes.PLAYER, Routes.ZAPPER].includes(route?.route) &&
        media.state === PlayerState.SEEKING
      ) {
        thunk.dispatch(seek());
      }
      thunk.dispatch(open(route));
      thunk.dispatch(pushFocus(Focus.FLOATING_MENU));
    } catch (error) {
      thunk.rejectWithValue(error);
    }
  }
);

export { closeFloatingMenu, openFloatingMenu };
